import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmptyState = ({ type, onAction }) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case 'no-users':
        return {
          icon: 'Users',
          title: 'No More Souls to Discover',
          description: 'You\'ve explored all compatible souls in your area. Try adjusting your filters or check back later for new cosmic connections.',
          actionText: 'Adjust Filters',
          actionIcon: 'Filter'
        };
      case 'no-matches':
        return {
          icon: 'Search',
          title: 'Expand Your Cosmic Reach',
          description: 'No matches found with current filters. Consider widening your search criteria to discover more astrological connections.',
          actionText: 'Broaden Search',
          actionIcon: 'Settings'
        };
      case 'profile-incomplete':
        return {
          icon: 'UserX',
          title: 'Complete Your Cosmic Profile',
          description: 'To find the best astrological matches, please complete your birth information and profile details.',
          actionText: 'Complete Profile',
          actionIcon: 'Edit'
        };
      default:
        return {
          icon: 'Sparkles',
          title: 'Cosmic Loading',
          description: 'The universe is aligning to bring you perfect matches...',
          actionText: 'Refresh',
          actionIcon: 'RefreshCw'
        };
    }
  };

  const content = getEmptyStateContent();

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/20 rounded-full animate-gentle-pulse"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-secondary/30 rounded-full animate-gentle-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-accent/20 rounded-full animate-gentle-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-primary/30 rounded-full animate-gentle-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Main Icon */}
      <div className="relative mb-8">
        <div className="w-24 h-24 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center mb-4 animate-gentle-pulse">
          <Icon name={content.icon} size={48} color="#4A5FBF" />
        </div>
        
        {/* Orbital rings */}
        <div className="absolute inset-0 border-2 border-primary/10 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
        <div className="absolute inset-2 border-2 border-secondary/10 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
      </div>

      {/* Content */}
      <div className="max-w-md space-y-4 relative z-10">
        <h2 className="text-2xl font-heading font-bold text-text-primary">
          {content.title}
        </h2>
        
        <p className="text-text-secondary leading-relaxed">
          {content.description}
        </p>

        {/* Astrological Quote */}
        <div className="glass bg-surface/30 rounded-lg p-4 my-6">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Quote" size={16} color="#F59E0B" />
            <span className="text-sm font-medium text-accent">Cosmic Wisdom</span>
          </div>
          <p className="text-sm text-text-secondary italic">
            "The universe is not only stranger than we imagine, it is stranger than we can imagine. But within this mystery lies the perfect harmony of souls destined to meet."
          </p>
        </div>

        {/* Action Button */}
        {onAction && (
          <Button
            variant="primary"
            iconName={content.actionIcon}
            onClick={onAction}
            className="mt-6"
          >
            {content.actionText}
          </Button>
        )}

        {/* Additional Actions */}
        <div className="flex justify-center space-x-4 mt-6">
          <Button
            variant="ghost"
            iconName="RefreshCw"
            onClick={() => window.location.reload()}
            className="text-sm"
          >
            Refresh
          </Button>
          <Button
            variant="ghost"
            iconName="Home"
            onClick={() => window.location.href = '/dashboard'}
            className="text-sm"
          >
            Go Home
          </Button>
        </div>
      </div>

      {/* Constellation Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <svg className="w-full h-full" viewBox="0 0 400 400">
          <defs>
            <pattern id="constellation" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1" fill="currentColor" />
              <circle cx="80" cy="30" r="1" fill="currentColor" />
              <circle cx="50" cy="70" r="1" fill="currentColor" />
              <line x1="20" y1="20" x2="80" y2="30" stroke="currentColor" strokeWidth="0.5" />
              <line x1="80" y1="30" x2="50" y2="70" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#constellation)" />
        </svg>
      </div>
    </div>
  );
};

export default EmptyState;